import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Pagination, Row } from "react-bootstrap";
import api from "../../../../utils/ApiMethod";
import PageTitle from "../../../../components/PageTitle";
import Spinner from "../../../../components/Spinner";
import Table from "../../../../components/Table";
import StatisticsWidget from "../../../widgets/StatisticsWidget";
import UpdateCrcModal from "./UpdateCrcModal";
import AddCrcModal from "./AddCrcModal";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;

const AllCRC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalCRC: 0,
    totalBlocks: 0,
    totalDistricts: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Debounce the search box so every keystroke doesn't fire a request,
  // and jump back to page 1 whenever the search term actually changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchCrcList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.set("search", search);

      const response = await api.fetchDataAuth(
        `/get/all/crc/list?${params.toString()}`
      );
      setData(response?.data?.clusters || []);
      setTotal(response?.data?.total ?? 0);
      setTotalPages(response?.data?.totalPages ?? 1);
    } catch (err) {
      console.error("Error fetching CRC list:", err);
      setError("Failed to fetch CRC list. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchCrcList();
  }, [fetchCrcList]);

  // Dashboard card counts come from a dedicated stats endpoint (three
  // index-covered countDocuments() calls) instead of fetching the full CRC
  // and block lists client-side just to read their length. The Add CRC
  // modal fetches the actual block list itself, lazily, only once someone
  // opens it — most visitors to this page never will.
  useEffect(() => {
    const fetchStats = async () => {
      setLoadingStats(true);
      try {
        const response = await api.fetchDataAuth("/get/region/stats");
        setStats({
          totalCRC: response?.data?.totalCRC ?? 0,
          totalBlocks: response?.data?.totalBlocks ?? 0,
          totalDistricts: response?.data?.totalDistricts ?? 0,
        });
      } catch (err) {
        console.error("Error fetching region stats:", err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "S.NO",
        accessor: "index",
        Cell: ({ row }: { row: any }) => (
          <span className="text-muted">
            {(page - 1) * limit + row.index + 1}
          </span>
        ),
        sort: false,
      },
      {
        Header: "CRC Name",
        accessor: "region_name",
        Cell: ({ value }: { value: string }) => (
          <span className="fw-semibold text-dark">{value}</span>
        ),
        sort: false,  
      },
      {
        Header: "Block Name Code",
        accessor: "parent_region",
        Cell: ({ row }: { row: any }) => (
          <span className="badge bg-light text-dark border fw-normal px-2 py-1">
            {row?.values?.parent_region?.region_name || "—"}
          </span>
        ),
        sort: false,
      },
      {
        Header: "Action",
        accessor: "b",
        Cell: ({ row }: { row: any }) => <UpdateCrcModal data={row.original} />,
        sort: false,
      },
    ],
    [page, limit]
  );

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  // A short, centered window of page numbers instead of one item per page —
  // matters once there are hundreds of pages across 15k+ CRCs.
  const pageWindow = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const items = [];
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  }, [page, totalPages]);

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          {
            label: "CRC",
            path: "/all-crc/list",
            active: true,
          },
        ]}
        title={"CRC"}
      />

      <Row>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="primary"
            title="Total CRC"
            stats={loadingStats ? "—" : stats.totalCRC.toLocaleString()}
            icon="layers"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="success"
            title="Total Blocks"
            stats={loadingStats ? "—" : stats.totalBlocks.toLocaleString()}
            icon="map"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="info"
            title="Total Districts"
            stats={loadingStats ? "—" : stats.totalDistricts.toLocaleString()}
            icon="globe"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="warning"
            title="Current Page"
            stats={`${page} / ${totalPages}`}
            icon="file-text"
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h5 className="mb-0">CRC List</h5>
              <AddCrcModal onAdded={fetchCrcList} />
            </Card.Header>
            <Card.Body>
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <Form.Control
                  type="search"
                  placeholder="Search CRC by name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-auto"
                  style={{ minWidth: 260 }}
                />
                <Form.Select
                  value={limit}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="w-auto"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </Form.Select>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : (
                <>
                  <Table
                    columns={columns}
                    data={data}
                    isSortable={false}
                    pagination={false}
                    isSearchable={false}
                    tableClass="table-striped table-hover align-middle mb-0"
                    theadClass="table-light"
                  />

                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                    <span className="text-muted small">
                      {total === 0
                        ? "No CRC found"
                        : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
                    </span>
                    {totalPages > 1 && (
                      <Pagination className="mb-0">
                        <Pagination.First
                          disabled={page === 1}
                          onClick={() => setPage(1)}
                        />
                        <Pagination.Prev
                          disabled={page === 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                        {pageWindow.map((p) => (
                          <Pagination.Item
                            key={p}
                            active={p === page}
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={page === totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        />
                        <Pagination.Last
                          disabled={page === totalPages}
                          onClick={() => setPage(totalPages)}
                        />
                      </Pagination>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AllCRC;
